import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Euro, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthOptions() {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('bg-BG', { year: 'numeric', month: 'long' });
    months.push({ value, label });
  }
  return months;
}

export const PricesTab = ({ employees }) => {
  const [monthlyRates, setMonthlyRates] = useState([]);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [rateForm, setRateForm] = useState({ employee_id: '', month: getCurrentMonth(), price_per_minute: '' });

  useEffect(() => { loadMonthlyRates(); }, []);

  const loadMonthlyRates = async () => {
    try {
      const response = await axios.get(`${API}/monthly-rates`);
      setMonthlyRates(response.data);
    } catch {
      console.error('Error loading monthly rates');
    }
  };

  // Auto-prefill: when employee or month changes, suggest the most recent known price
  const getPrefillPrice = (employee_id, month) => {
    if (!employee_id) return '';
    const empRates = monthlyRates
      .filter(r => r.employee_id === employee_id && r.month <= month)
      .sort((a, b) => b.month.localeCompare(a.month));
    return empRates.length > 0 ? String(empRates[0].price_per_minute) : '';
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/monthly-rates`, {
        employee_id: rateForm.employee_id,
        month: rateForm.month,
        price_per_minute: parseFloat(rateForm.price_per_minute)
      });
      setShowRateDialog(false);
      setRateForm({ employee_id: '', month: getCurrentMonth(), price_per_minute: '' });
      loadMonthlyRates();
      toast.success('Цената е запазена');
    } catch {
      toast.error('Грешка при запазване');
    }
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    try {
      await axios.delete(`${API}/monthly-rates/${rateId}`);
      loadMonthlyRates();
      toast.success('Цената е изтрита');
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-font text-xl font-bold uppercase">Месечни цени на минута</h2>
        <Button data-testid="add-rate-button" onClick={() => { setRateForm({ employee_id: '', month: getCurrentMonth(), price_per_minute: '' }); setShowRateDialog(true); }} className="bg-amber-600 hover:bg-amber-700 font-bold uppercase">
          <Euro className="w-5 h-5 mr-2" />Задай цена
        </Button>
      </div>
      <p className="text-slate-600 mb-4">Тук задавате цена на минута за всеки работник за всеки месец. Цената се използва при изчисляване на заплатата в "Обобщение".</p>

      {monthlyRates.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Euro className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg">Няма зададени цени</p>
          <p className="text-sm mt-2">Натиснете "Задай цена" за да добавите</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs">Служител</TableHead>
              <TableHead className="uppercase text-xs">Месец</TableHead>
              <TableHead className="uppercase text-xs">Цена/мин (EUR)</TableHead>
              <TableHead className="uppercase text-xs">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthlyRates.sort((a, b) => b.month.localeCompare(a.month)).map((rate) => (
              <TableRow key={rate.id}>
                <TableCell className="font-medium">{rate.employee_name}</TableCell>
                <TableCell className="mono-font">{rate.month}</TableCell>
                <TableCell className="mono-font font-bold text-amber-600">{rate.price_per_minute} EUR</TableCell>
                <TableCell>
                  <Button onClick={() => handleDeleteRate(rate.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading-font text-xl uppercase">Задай цена на минута</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveRate} className="space-y-4">
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Служител *</Label>
              <Select value={rateForm.employee_id} onValueChange={(v) => setRateForm(f => ({ ...f, employee_id: v, price_per_minute: getPrefillPrice(v, f.month) }))}>
                <SelectTrigger><SelectValue placeholder="Изберете служител" /></SelectTrigger>
                <SelectContent>{employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Месец *</Label>
              <Select value={rateForm.month} onValueChange={(v) => setRateForm(f => ({ ...f, month: v, price_per_minute: getPrefillPrice(f.employee_id, v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{getMonthOptions().map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Цена на минута (EUR) *</Label>
              <Input type="number" step="0.001" min="0" value={rateForm.price_per_minute} onChange={(e) => setRateForm({ ...rateForm, price_per_minute: e.target.value })} className="border-2" placeholder="0.00" required />
              {rateForm.price_per_minute && rateForm.employee_id && (
                <p className="text-xs text-slate-500 mt-1">Предложена стойност от последно зададената цена</p>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={!rateForm.employee_id || !rateForm.price_per_minute} className="flex-1 bg-amber-600 hover:bg-amber-700 font-bold uppercase">Запази</Button>
              <Button type="button" onClick={() => setShowRateDialog(false)} variant="outline" className="flex-1 border-2 font-semibold uppercase">Отказ</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
